'use client';

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Copy, RefreshCw, Check, Sparkles, FileText, Code } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { Toaster } from '@/components/ui/toaster';
import { ToolPageIntro } from '../../components/seo/ToolPageIntro';
import { InformationalContent } from '../../components/seo/InformationalContent';
import { jsonToYamlContent } from '../../data/json-to-yaml-content';
import { cn } from '@/lib/utils';

const sampleJson = `{
  "person": {
    "name": "John Doe",
    "age": 30,
    "isActive": true,
    "contact": {
      "email": "john@example.com",
      "phone": "+1 (555) 123-4567"
    },
    "address": {
      "street": "123 Main St",
      "city": "New York",
      "country": "USA",
      "coordinates": [40.7128, -74.0060]
    },
    "skills": ["JavaScript", "TypeScript", "React", "Node.js"],
    "experience": [
      {
        "company": "Tech Corp",
        "position": "Senior Developer",
        "years": 5
      },
      {
        "company": "Web Solutions",
        "position": "Frontend Developer",
        "years": 3
      }
    ]
  }
}`;

const sampleYaml = `person:
  name: John Doe
  age: 30
  isActive: true
  contact:
    email: john@example.com
    phone: +1 (555) 123-4567
  address:
    street: 123 Main St
    city: New York
    country: USA
    coordinates:
      - 40.7128
      - -74.006
  skills:
    - JavaScript
    - TypeScript
    - React
    - Node.js
  experience:
    - company: Tech Corp
      position: Senior Developer
      years: 5
    - company: Web Solutions
      position: Frontend Developer
      years: 3`;

function formatJson(jsonString: string): string {
    try {
        const obj = JSON.parse(jsonString);
        return JSON.stringify(obj, null, 2);
    } catch (e) {
        return jsonString;
    }
}

function jsonToYaml(jsonString: string): string {
    try {
        // Remove any trailing commas that might be in the JSON
        const sanitizedJson = jsonString.replace(/,\s*([}\]])/g, '$1');
        const jsonObj = JSON.parse(sanitizedJson);
        return convertToYaml(jsonObj);
    } catch (error) {
        throw new Error(`Invalid JSON: ${error instanceof Error ? error.message : 'Unknown error'}`);
    }
}

function convertToYaml(obj: any, indent = 0): string {
    const indentStr = '  '.repeat(indent);
    let result = '';

    if (obj === null || obj === undefined) {
        return 'null';
    }

    switch (typeof obj) {
        case 'string':
            // Check if the string needs quotes
            if (obj.includes(':') || obj.includes('\n') || obj.trim() !== obj ||
                obj === 'true' || obj === 'false' || obj === 'null' ||
                !isNaN(Number(obj)) && obj.toString().trim() === String(Number(obj))) {
                // Escape double quotes and wrap in quotes
                return `"${obj.replace(/"/g, '\\"')}"`;
            }
            return obj;

        case 'number':
        case 'boolean':
            return obj.toString();

        case 'object':
            if (Array.isArray(obj)) {
                if (obj.length === 0) return '[]';

                // Check if array contains objects
                const hasObjects = obj.some(item => typeof item === 'object' && item !== null);

                if (hasObjects) {
                    return obj.map(item =>
                        `- ${convertToYaml(item, indent + 1).trim()}`
                    ).join('\n' + indentStr);
                } else {
                    return obj.map(item =>
                        `- ${convertToYaml(item, indent + 1)}`
                    ).join('\n' + indentStr);
                }
            } else {
                const keys = Object.keys(obj);
                if (keys.length === 0) return '{}';

                return keys.map((key, i) => {
                    const value = obj[key];
                    const valueStr = convertToYaml(value, indent + 1);
                    const colon = valueStr.includes('\n') ? ':' : ': ';
                    return `${key}${colon}${valueStr}`;
                }).join('\n' + indentStr);
            }

        default:
            return String(obj);
    }
}

export default function JsonToYamlTool() {
    const [input, setInput] = useState('');
    const [output, setOutput] = useState('');
    const [error, setError] = useState('');
    const [isCopied, setIsCopied] = useState(false);
    const [isPrettyJson, setIsPrettyJson] = useState(true);
    const { toast } = useToast();

    const convertJsonToYaml = (jsonString: string) => {
        try {
            if (!jsonString.trim()) {
                setOutput('');
                setError('');
                return;
            }

            const yamlString = jsonToYaml(jsonString);
            setOutput(yamlString);
            setError('');
        } catch (err) {
            setError(`Error: ${err instanceof Error ? err.message : 'Invalid JSON'}`);
            setOutput('');
        }
    };

    useEffect(() => {
        const timer = setTimeout(() => {
            convertJsonToYaml(input);
        }, 300);

        return () => clearTimeout(timer);
    }, [input]);

    useEffect(() => {
        if (input && isPrettyJson) {
            try {
                const formatted = formatJson(input);
                if (formatted !== input) {
                    setInput(formatted);
                }
            } catch (e) {
                // Ignore formatting errors
            }
        }
    }, [isPrettyJson]);

    const handleCopy = () => {
        if (!output) return;
        navigator.clipboard.writeText(output);
        setIsCopied(true);
        toast({
            title: 'Copied to clipboard!',
            description: 'The YAML has been copied to your clipboard.',
            variant: 'default',
        });
        setTimeout(() => setIsCopied(false), 2000);
    };

    const handleClear = () => {
        setInput('');
        setOutput('');
        setError('');
    };

    const loadSample = () => {
        setInput(sampleJson);
    };

    const toggleJsonFormatting = () => {
        if (input) {
            try {
                if (isPrettyJson) {
                    // Minify JSON
                    const minified = JSON.stringify(JSON.parse(input));
                    setInput(minified);
                } else {
                    // Pretty print JSON
                    const pretty = formatJson(input);
                    setInput(pretty);
                }
            } catch (e) {
                // Ignore errors
            }
        }
        setIsPrettyJson(!isPrettyJson);
    };

    return (
        <div className="container mx-auto px-4 py-8 max-w-7xl">
            <div className="text-center mb-10 space-y-2">
                <div className="inline-flex items-center justify-center px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-3">
                    <Sparkles className="h-4 w-4 mr-2" />
                    Developer Tool
                </div>
                <ToolPageIntro title="JSON to YAML Converter">
                Convert JSON data to YAML format. Free online JSON to YAML converter with proper formatting.
            </ToolPageIntro>
            </div>

            <div className="w-full max-w-7xl mx-auto grid grid-cols-12 gap-2 lg:gap-6 items-stretch">
                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        JSON Input
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Paste your JSON data here
                                    </CardDescription>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={loadSample}
                                        className="gap-1.5"
                                    >
                                        <Sparkles className="h-3.5 w-3.5" />
                                        Sample
                                    </Button>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={toggleJsonFormatting}
                                        className="gap-1.5"
                                    >
                                        {isPrettyJson ? (
                                            <FileText className="h-3.5 w-3.5" />
                                        ) : (
                                            <Code className="h-3.5 w-3.5" />
                                        )}
                                        {isPrettyJson ? 'Minify' : 'Beautify'}
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        onClick={handleClear}
                                        disabled={!input}
                                        className="text-muted-foreground hover:text-foreground"
                                    >
                                        Clear
                                    </Button>
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="flex-1 flex flex-col">
                                <Textarea
                                    value={input}
                                    onChange={(e) => setInput(e.target.value)}
                                    placeholder={
                                        'Paste your JSON here...\n{\n  "example": {\n    "key": "value",\n    "items": [1, 2, 3]\n  }\n}'
                                    }
                                    className="min-h-[400px] font-mono text-sm border-0 focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none"
                                    spellCheck={false}
                                />
                            </div>
                        </CardContent>
                    </Card>
                </div>

                <div className="col-span-12 lg:col-span-6 h-full flex flex-col">
                    <Card className="border-0 shadow-lg overflow-hidden flex-1 flex flex-col h-full">
                        <CardHeader className="bg-gradient-to-r from-primary/5 to-primary/10 border-b">
                            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                                <div>
                                    <CardTitle className="text-xl font-semibold">
                                        YAML Output
                                    </CardTitle>
                                    <CardDescription className="mt-1">
                                        Your converted YAML will appear here
                                    </CardDescription>
                                </div>
                                <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={handleCopy}
                                    disabled={!output}
                                    className={cn(
                                        'gap-1.5 transition-all',
                                        isCopied
                                            ? 'bg-green-500/10 border-green-500/20 text-green-600 hover:bg-green-500/20'
                                            : 'hover:bg-primary/5'
                                    )}
                                >
                                    {isCopied ? (
                                        <Check className="h-4 w-4" />
                                    ) : (
                                        <Copy className="h-4 w-4" />
                                    )}
                                    {isCopied ? 'Copied!' : 'Copy'}
                                </Button>
                            </div>
                        </CardHeader>
                        <CardContent className="p-0 flex-1 flex flex-col">
                            <div className="relative flex-1 flex flex-col min-h-[400px]">
                                <Textarea
                                    value={output}
                                    readOnly
                                    className={cn(
                                        'flex-1 font-mono text-sm border-0 bg-muted/20',
                                        'focus-visible:ring-0 focus-visible:ring-offset-0 rounded-none resize-none',
                                        error && 'text-destructive/70',
                                        'whitespace-pre' // Preserve indentation
                                    )}
                                    placeholder="Your converted YAML will appear here..."
                                />
                                {!output && !error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-muted-foreground/50 text-sm text-center px-4">
                                            Your converted YAML will appear here
                                        </p>
                                    </div>
                                )}
                                {error && (
                                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                                        <p className="text-destructive/70 text-sm text-center px-4">
                                            {error}
                                        </p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </div>
            </div>

            {/* SEO CONTENT */}
            <div className="mt-16">
                <InformationalContent
                    title={jsonToYamlContent.title}
                    description={jsonToYamlContent.description}
                    content={jsonToYamlContent.content}
                    faqs={jsonToYamlContent.faqs}
                    relatedTools={jsonToYamlContent.relatedTools}
                />
            </div>

            <Toaster />
        </div>
    );
}
